import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

const LS_TOKEN_KEY = 'auth_token';
const LS_USER_KEY = 'auth_user';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_USER_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [isLoading, setIsLoading] = useState(true);

    const isAuthenticated = !!user;
    const isAdmin = user?.role === 'admin';

    // Initialize: check if stored token is still valid
    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem(LS_TOKEN_KEY);
            if (token) {
                try {
                    const userData = await apiService.getCurrentUser();
                    setUser(userData);
                    localStorage.setItem(LS_USER_KEY, JSON.stringify(userData));
                } catch {
                    // Token invalid, clear storage
                    localStorage.removeItem(LS_TOKEN_KEY);
                    localStorage.removeItem(LS_USER_KEY);
                    setUser(null);
                }
            }
            setIsLoading(false);
        };
        initAuth();
    }, []);

    const saveAuth = useCallback((token, userData) => {
        localStorage.setItem(LS_TOKEN_KEY, token);
        localStorage.setItem(LS_USER_KEY, JSON.stringify(userData));
        setUser(userData);
    }, []);

    const clearAuth = useCallback(() => {
        localStorage.removeItem(LS_TOKEN_KEY);
        localStorage.removeItem(LS_USER_KEY);
        setUser(null);
    }, []);

    const login = useCallback(async (email, password) => {
        const response = await apiService.login(email, password);
        saveAuth(response.token, response.user);
        return response;
    }, [saveAuth]);

    const register = useCallback(async (email, password) => {
        const response = await apiService.register(email, password);
        saveAuth(response.token, response.user);
        return response;
    }, [saveAuth]);

    const logout = useCallback(async () => {
        try {
            await apiService.logout();
        } catch {
            // Ignore logout API errors
        }
        clearAuth();
    }, [clearAuth]);

    const changePassword = useCallback(async (oldPassword, newPassword) => {
        return apiService.changePassword(oldPassword, newPassword);
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated,
            isAdmin,
            isLoading,
            login,
            register,
            logout,
            changePassword,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export default AuthContext;
